/**
 * The practice settings tab for lens extras ("Lens extras" in the settings dialog, beside the premium
 * lens prices): the optional items offered on glasses and contact lens jobs, and their prices. The
 * glasses job dialog's "Edit extras & prices" opens the same tab.
 *
 * One row per kind of extra, each item a small card: tick to offer it, its name and its price, and
 * only where the job dialog shows them a description, colour, "on every lens" or "suits" choice.
 * Anything can be added; nothing is deleted - an item is unticked instead, since jobs that used it
 * still need its name and price. The built-in choices (standard lens, clear, no mirror, no fitting
 * fee) are always offered and free.
 *
 * Saved through PracticeService; the API merges each list, so an item left out stays, unoffered.
 */
app.component("lensDispensingSettings", {
	templateUrl: "app/src/js/components/lensDispensing/lensDispensingSettings.html",
	controller: "lensDispensingSettingsController",
	bindings: { practiceId: "<" },
});

app.controller("lensDispensingSettingsController", [
	"$scope",
	"$timeout",
	"PracticeService",
	"LensDispensingStore",
	"Session",
	function ($scope, $timeout, PracticeService, Store, Session) {
		const $ctrl = this;

		// The rows, in the order the job dialogs show them. `hint`, `colour`, `included` and `suits`
		// say which extra fields an item in the row has; `icon` is what a new item is shown with.
		$scope.glassesRows = [
			{ key: "lensTypes", label: "Lens types", help: "Shown with the lens on every glasses job.", hint: true, colour: true, icon: "fa-circle-o", newColour: "#6b7078" },
			{ key: "tints", label: "Tints", help: "The colour is how the tint looks in the preview.", colour: true, gradient: true, newColour: "#8a8f98" },
			{ key: "coatings", label: "Coatings", help: "Tick \"On every lens\" for coatings included at no charge.", hint: true, included: true, icon: "fa-shield" },
			{ key: "mirrors", label: "Mirror finishes", colour: true, newColour: "#c9d1db" },
			{ key: "accessories", label: "Add-ons", icon: "fa-plus-circle" },
		];
		$scope.contactsRows = [
			{ key: "fittingFees", label: "Fitting fees", help: "Billed on the first contact lens order from a prescription.", hint: true, icon: "fa-eye" },
			{ key: "careProducts", label: "Care products", help: "Only offered with the lenses they suit.", hint: true, suits: true, icon: "fa-tint" },
		];
		$scope.suitsOptions = [
			{ value: null, label: "Any lenses" },
			{ value: "soft", label: "Soft lenses" },
			{ value: "rigid", label: "Hard lenses" },
		];

		// Those the API lets change prices; everyone else sees the settings read-only.
		$scope.canEdit = false;
		$scope.state = { loading: true, error: null, saving: false };
		$scope.saveStatus = "";
		$scope.settings = null;
		let saved = "";

		const snapshot = () => angular.toJson($scope.settings);

		$ctrl.$onInit = function () {
			$scope.practiceId = $ctrl.practiceId || (Session.practice && Session.practice.id);
			const practice = (Session.practiceOptions || []).find((p) => p.id === $scope.practiceId) || Session.practice || {};
			$scope.practiceName = practice.name || "";
			$scope.canEdit = Session.hasPermissionAt(practice, "PRACTICE_LENS_CATALOGUE_MANAGE");
			load();
		};

		function adopt(settings) {
			$scope.settings = settings;
			saved = snapshot();
		}

		function load() {
			$scope.state.loading = true;
			$scope.state.error = null;
			PracticeService.queryLensDispensingSettings($scope.practiceId)
				.then((resp) => adopt(resp.data))
				.catch((resp) => ($scope.state.error = Store.errorMessage(resp, "The lens extras could not be loaded.")))
				.finally(() => ($scope.state.loading = false));
		}
		$scope.reload = load;

		$scope.isDirty = () => !!$scope.settings && snapshot() !== saved;
		$scope.offered = (row) => ($scope.settings[row.key] || []).filter((i) => i.active).length;

		// Drop a stale "saved" as soon as anything is changed again.
		$scope.$watch($scope.isDirty, function (dirty) {
			if (dirty && $scope.saveStatus.indexOf("Error") === -1) $scope.saveStatus = "";
		});

		$scope.add = function (row) {
			if (!$scope.canEdit) return;
			const item = { id: null, name: "", hint: null, price: 0, active: true, icon: row.icon || null, colour: row.newColour || null, suits: null, reusable: row.suits ? true : null };
			$scope.settings[row.key].push(item);
			$timeout(function () {
				const inputs = document.querySelectorAll(`.lx-row[data-row="${row.key}"] .lx-name`);
				if (inputs.length) inputs[inputs.length - 1].focus();
			});
		};

		// Only an item that has never been saved can go; a saved one is unticked instead.
		$scope.discard = function (row, item) {
			const list = $scope.settings[row.key];
			const i = list.indexOf(item);
			if (i >= 0 && !item.id) list.splice(i, 1);
		};

		$scope.setIncluded = function (item) {
			if (item.included) item.price = 0;
		};

		// What stops a save: an offered item without a name.
		$scope.problem = function () {
			if (!$scope.settings) return null;
			const rows = $scope.glassesRows.concat($scope.contactsRows);
			for (let r = 0; r < rows.length; r++) {
				const unnamed = ($scope.settings[rows[r].key] || []).some((i) => !(i.name || "").trim());
				if (unnamed) return `Each of the ${rows[r].label.toLowerCase()} needs a name.`;
			}
			return null;
		};

		$scope.undo = function () {
			$scope.settings = angular.fromJson(saved);
			$scope.saveStatus = "";
		};

		$scope.save = function () {
			if (!$scope.canEdit || !$scope.isDirty() || $scope.problem() || $scope.state.saving) return;
			$scope.state.saving = true;
			$scope.saveStatus = "Saving...";
			PracticeService.updateLensDispensingSettings($scope.settings, $scope.practiceId)
				.then(function (resp) {
					adopt(resp.data);
					// The dispensing dialogs read the practice they are in from the store.
					if (Session.practice && Session.practice.id === $scope.practiceId) Store.applySettings(resp.data);
					$scope.saveStatus = "Lens extras saved";
				})
				.catch((resp) => ($scope.saveStatus = "Error: " + Store.errorMessage(resp, "the lens extras could not be saved.")))
				.finally(() => ($scope.state.saving = false));
		};
	},
]);
